


















































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { PathViewModel } from '@/features/bots/models/designer';
import { Action, Getter, Mutation } from 'vuex-class';
import { mixin as clickaway } from 'vue-clickaway';
import { dropDownHelper } from '@/shared/components/dropDownMenu/dropDownHelper';
import { ADD_PATH, SET_CURRENT_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { TransitionViewModel } from '../../models/designer/transitionViewModel';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import PathIcon from 'vue-material-design-icons/CallSplit.vue';
import FlowIcon from 'vue-material-design-icons/Waves.vue';
import shortid from 'shortid';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import Toast from '@/shared/services/toast';
import AddFlowModal from '@/shared/components/AddFlowModal.vue';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import { TransitionPickerOption } from '../../models/designer/transitionPickerOption';
import { TransitionPickerConfig } from '../../models/designer/transitionPickerConfig';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import { AnswerListItemViewModel } from '../../models/answers/answerListItemViewModel';

@Component({
	components: {
		PathIcon,
		FlowIcon,
		AddIcon,
		AddFlowModal
	},
	mixins: [clickaway]
})
export default class TransitionPicker extends Vue {
	@Action(LOAD_ANSWERS) public loadFlows: () => Promise<FlowListItemViewModel[]>;
	@Action(LOAD_FLOWS) public loadAnswers: () => Promise<AnswerListItemViewModel[]>;
	@Getter public transitionPickerConfigForAnswer: TransitionPickerConfig;
	@Getter public transitionPickerConfigForFlow: TransitionPickerConfig;
	@Getter public transitionPickerFixedConfig: TransitionPickerConfig;
	@Getter public isTransitionPayloadInvalid: (payload: string) => boolean;
	@Prop() public transition: TransitionViewModel;

	@Prop({ default: false }) public fixed: boolean;
	@Prop({ default: true }) public showNoneTransition: boolean;

	@Mutation(SET_CURRENT_PATH) public setCurrentPath: (id: string) => void;
	@Mutation(ADD_PATH) public addPath: (path: PathViewModel) => void;

	public selectedLabel: string = '';
	public isOpen = false;
	public isUp = false;
	public selectedTransition: TransitionViewModel;
	public showModal: boolean = false;
	public loaded: boolean = false;

	@Watch('transition', { immediate: true })
	public async onValueChanged(value: TransitionViewModel, previous: TransitionViewModel) {
		this.selectedTransition = value;
	}

	public async mounted() {
		await this.loadFlowsAndAnswers();
	}

	public get pathOptions(): TransitionPickerOption[] {
		return this.options.filter(x => x.type === TransitionType.Path).sort((a, b) => a.name.localeCompare(b.name));
	}

	public get flowOptions(): TransitionPickerOption[] {
		return this.options.filter(x => x.type === TransitionType.Flow).sort((a, b) => a.name.localeCompare(b.name));
	}

	public get answerOptions(): TransitionPickerOption[] {
		return this.options.filter(x => x.type === TransitionType.Answer).sort((a, b) => a.name.localeCompare(b.name));
	}

	public get noneTransition(): TransitionPickerOption | undefined {
		return this.options.find(x => x.type === TransitionType.None);
	}

	public get config(): TransitionPickerConfig {
		if (this.fixed) {
			return this.transitionPickerFixedConfig;
		}
		if (this.isOnAnswersPage()) {
			return this.transitionPickerConfigForAnswer;
		}
		return this.transitionPickerConfigForFlow;
	}

	public get options(): TransitionPickerOption[] {
		return this.config?.options || [];
	}

	public get canAddNewFlow() {
		return this.config.currentStatus !== FlowStatus.Live;
	}

	public get isPathOptionsVisible() {
		return (this.pathOptions && this.pathOptions.length > 0) || this.config.allowAddPath;
	}

	public get isFlowOptionsVisible() {
		return (this.flowOptions && this.flowOptions.length > 0) || this.canAddNewFlow;
	}

	public get isAnswerOptionsVisible() {
		return this.answerOptions && this.answerOptions.length > 0;
	}

	public get isGoToButtonDisabled() {
		return !this.loaded || !this.isTransitionValid;
	}

	public get isTransitionValid() {
		return this.transition && this.transition.payload && !this.isTransitionPayloadInvalid(this.transition.payload);
	}

	public getSelectedOptionName(): string {
		if (!this.loaded) {
			return '';
		}
		if (this.selectedTransition && this.selectedTransition.payload) {
			if (this.isTransitionPayloadInvalid(this.selectedTransition.payload)) {
				return 'Invalid transition';
			}
		}
		if (!this.selectedLabel) {
			this.selectedLabel = this.options.find(x => x.id === this.selectedTransition?.payload)?.name || '';
		}
		if (!this.selectedLabel) {
			return 'Choose a transition';
		}

		return this.selectedLabel;
	}

	public closePicker() {
		this.isOpen = false;
		this.isUp = false;
	}

	public togglePicker() {
		// don't allow opening the menu/choosing a path if the picker should be 'fixed'
		if (this.fixed) {
			return;
		}

		this.isOpen = !this.isOpen;

		this.$nextTick(() => (this.isUp = dropDownHelper.isDropDownUp()));
	}

	public goToTransition() {
		if (typeof this.selectedTransition.payload === 'string') {
			if (this.selectedTransition.type === TransitionType.Path) {
				this.setCurrentPath(this.selectedTransition.payload);
			} else if (this.selectedTransition.type === TransitionType.Flow) {
				this.goToFlow();
			} else if (this.selectedTransition.type === TransitionType.Answer) {
				this.goToAnswer();
			}
		}
	}

	public chooseOption(option: TransitionPickerOption) {
		this.selectedLabel = option.name;
		this.chooseTransition(option.id, option.type);
		this.closePicker();
	}

	public chooseFlow(flow: FlowListItemViewModel) {
		this.selectedLabel = flow.name;
		this.chooseTransition(flow.id, TransitionType.Flow);
	}

	public chooseTransition(id: string, transitionType: TransitionType) {
		this.selectedTransition = {
			payload: id,
			type: transitionType
		} as TransitionViewModel;

		this.$emit('transition-selected', this.selectedTransition);
	}

	public isTypeSelected(type: TransitionType) {
		return this.selectedTransition && this.selectedTransition.type === type;
	}

	public async goToFlow() {
		try {
			if (this.selectedTransition && this.selectedTransition.payload) {
				this.$router.push({
					name: 'bots-flow-designer',
					params: { flowId: this.selectedTransition.payload }
				});
			} else {
				Toast.showError('Can\'t find Flow');
			}
		} catch (err: any) {
			Toast.showError(err.message);
		}
	}

	public async goToAnswer() {
		try {
			if (this.selectedTransition && this.selectedTransition.payload) {
				this.$router.push({
					name: 'bots-answer-editor',
					params: { id: this.selectedTransition.payload }
				});
			}
		} catch (err: any) {
			Toast.showError(err.message);
		}
	}

	public addNewPath() {
		const newPath = {
			id: `path-${shortid.generate()}`,
			name: `New Path`,
			states: []
		} as PathViewModel;

		this.addPath(newPath);

		this.selectedLabel = newPath.name;

		this.selectedTransition = {
			type: TransitionType.Path,
			payload: newPath.id
		};
		this.$emit('transition-selected', this.selectedTransition);
	}

	public isNoneChosen() {
		return this.transition && this.transition.type === TransitionType.None;
	}

	private isOnAnswersPage() {
		return this.getRouteName() === 'bots-answer-editor';
	}

	private getRouteName() {
		return this.$router.currentRoute.name;
	}

	private async loadFlowsAndAnswers(): Promise<void> {
		this.loaded = false;
		try {
			await Promise.all([this.loadFlows(), this.loadAnswers()]);
		} catch (e: any) {
			Toast.showError(e.message);
		}
		this.loaded = true;
	}
}
